import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "sonner";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { computeHealthScore, scoreBand } from "@/lib/health";
import { AlertCircle, Loader2, Play, Pause, ShieldCheck, Download, Share2, Check } from "lucide-react";
import {
  LabLetterheadBottom,
  PrintRunningHeader,
  PrintRunningFooter,
  PatientMetaCard,
  ReportSection,
  SummaryTab,
  BiomarkersTab,
  CriticalTab,
  LifestyleTab,
  FollowUpTab,
  BiomarkerModal,
  getLetterhead,
} from "./SmartReport";
import { ClaimReportBanner, ClaimReportModal } from "@/components/report/ClaimReport";
import PublicSharedHeader from "@/components/report/PublicSharedHeader";
import { PrintDocument } from "@/pages/report/pdf/PrintDocument";
import { downloadReportWithCover } from "@/pages/report/pdf/downloadWithCover";
import ReportIntegrityFooter from "@/components/report/ReportIntegrityFooter";
import InteractiveBodyMap from "@/components/report/InteractiveBodyMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public (no-auth) share view. Uses the exact same single-scroll layout as the
 * authenticated Smart Report — letterhead header + patient details + stacked sections
 * (Summary, Biomarkers, Critical, Lifestyle) + letterhead footer with doctor signatures.
 */
export default function PublicSmartReport() {
  const { slug, token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedBio, setSelectedBio] = useState(null);

  const audioRef = useRef(null);
  const printRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [lang, setLang] = useState("en");
  const [claimOpen, setClaimOpen] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  // Meaningful PDF filename in PatientName_LabName_ReportID format.
  const pdfFilename = useMemo(() => {
    const slug = (s) => String(s || "").replace(/[^A-Za-z0-9]/g, "");
    const patient = slug(data?.report?.patient_name) || "Patient";
    const labName = slug(data?.lab?.name) || "EFGH";
    const shortId = String(data?.report?.id || token || "shared").slice(0, 6).toUpperCase() || "REPORT";
    return `${patient}_${labName}_${shortId}`;
  }, [data?.report?.patient_name, data?.lab?.name, data?.report?.id, token]);

  // Same PDF engine as the authenticated /report/:id — react-to-print clones the off-screen
  // <PrintDocument> into a hidden iframe. Deterministic pagination, running header/footer,
  // 10 mm safe pads, page numbers — identical output.
  const reactPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: pdfFilename,
    onPrintError: () => window.print(),
  });

  // Fetch public platform settings once to determine if cover-page is enabled for this lab.
  const [platformSettings, setPlatformSettings] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/platform-settings`).then((r) => setPlatformSettings(r.data)).catch(() => {});
  }, []);

  const coverPageEnabled =
    data?.lab?.id &&
    (platformSettings?.cover_page_enabled_labs || []).includes(data.lab.id) &&
    !!data?.lab?.cover_page_url;
  const [pdfBusy, setPdfBusy] = useState(false);

  /**
   * PDF download — primary path is the server-side Playwright renderer via
   * `/api/public/labs/{slug}/reports/{token}/pdf`. Falls back to client-side
   * cover merge (or browser print) if the server fails.
   */
  const handleDownloadPDF = async () => {
    if (!printRef.current) return toast.error("Report not ready yet");
    setPdfBusy(true);
    const t = toast.loading("Preparing PDF…");
    try {
      const url = `${API}/public/labs/${slug}/reports/${token}/pdf`;
      const resp = await fetch(url, { method: "GET" });
      if (!resp.ok) throw new Error(`Server PDF ${resp.status}`);
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `${pdfFilename}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(dlUrl), 5000);
      toast.success("PDF downloaded", { id: t });
      setPdfBusy(false);
      return;
    } catch (err) {
      console.warn("[PDF] server render failed, falling back:", err);
    }

    if (coverPageEnabled) {
      try {
        const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const coverUrl = data.lab.cover_page_url.startsWith("http")
          ? data.lab.cover_page_url
          : `${backend}${data.lab.cover_page_url}`;
        await downloadReportWithCover({
          docEl: printRef.current,
          letterhead: data?.lab?.letterhead || {},
          coverAbsoluteUrl: coverUrl,
          filename: pdfFilename,
        });
        toast.success("PDF downloaded", { id: t });
      } catch (e) {
        console.error("Client cover-merge failed:", e);
        toast.error("PDF export failed — opening print dialog", { id: t });
        try {
          const p = reactPrint();
          if (p && typeof p.then === "function") p.catch(() => window.print());
        } catch { window.print(); }
      }
    } else {
      toast.dismiss(t);
      try {
        const p = reactPrint();
        if (p && typeof p.then === "function") p.catch(() => window.print());
      } catch { window.print(); }
    }
    setPdfBusy(false);
  };

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const title = report?.patient_name ? `EFGH Smart Report — ${report.patient_name}` : "EFGH Smart Report";
    try {
      if (navigator.share) {
        await navigator.share({ title, text: "Smart health report from " + (lab?.name || "EFGH"), url });
        return;
      }
    } catch { /* user cancelled or unsupported */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 1600);
    } catch { /* no-op */ }
  };

  useEffect(() => {
    axios.get(`${API}/public/labs/${slug}/reports/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 404) {
          setError(detail === "Lab not found"
            ? "The lab that shared this report couldn't be found. It may have been deactivated."
            : "This report link is invalid, has expired, or has been revoked by the patient.");
        } else if (status === 403) {
          setError("This report is private. Please sign in to view it.");
        } else {
          setError("Could not load report. Please check your internet connection and try again.");
        }
      });
  }, [slug, token]);

  const lab = data?.lab;
  const report = data?.report;
  const smart = report?.smart_report;
  const healthScore = useMemo(() => computeHealthScore(smart), [smart]);
  const band = scoreBand(healthScore);

  const loadAudio = async (l) => {
    const which = l || lang;
    const has = which === "en" ? report?.has_audio_en : report?.has_audio_hi;
    if (!has) return null;
    const res = await fetch(`${API}/public/reports/${token}/audio?lang=${which}`);
    if (!res.ok) return null;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    setAudioUrl(url);
    return url;
  };
  const switchLang = async (l) => {
    if (l === lang) return;
    setLang(l);
    if (audioRef.current) { audioRef.current.pause(); setPlaying(false); }
    setAudioUrl(null); await loadAudio(l);
  };
  const togglePlay = async () => {
    if (!audioUrl) await loadAudio();
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { try { await audioRef.current.play(); setPlaying(true); } catch { setPlaying(false); } }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F7F8FA] p-4 sm:p-8">
        <Card className="p-6 sm:p-10 rounded-2xl text-center max-w-md w-full" data-testid="share-error">
          <AlertCircle className="w-10 h-10 mx-auto text-danger" />
          <div className="mt-3 font-heading font-semibold text-ink text-lg">Report unavailable</div>
          <p className="text-sm text-ink/60 mt-2">{error}</p>
          <div className="mt-6 space-y-2">
            <Link to="/login" className="block w-full">
              <Button className="w-full bg-ink text-white hover:bg-ink/90" data-testid="share-error-login">
                Sign in to view your reports
              </Button>
            </Link>
            <Link to="/" className="block w-full">
              <Button variant="outline" className="w-full" data-testid="share-error-home">
                Go to EFGH home
              </Button>
            </Link>
          </div>
          <div className="mt-5 pt-4 border-t border-border text-[11px] text-ink/40">
            If you think this is a mistake, ask the sender to re-share the link.
          </div>
        </Card>
      </div>
    );
  }
  if (!data || !smart) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-ink/40" /></div>;
  }

  const lh = getLetterhead(lab) || { header_height_mm: 24, footer_height_mm: 16 };
  const hH = lh.header_height_mm ?? 24;
  const fH = lh.footer_height_mm ?? 16;

  return (
    <div className="min-h-screen bg-white" data-testid="public-report">
      {/* Print CSS for the running header/footer on every printed page — mirrors the private route. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { background: white !important; }
          [data-testid="lab-letterhead-onscreen"], [data-testid="lab-letterhead-bottom-onscreen"], [data-testid="public-meta-bar"] { display: none !important; }
          .print-running-header { position: fixed !important; top: 0; left: 0; right: 0; z-index: 9999; background: white !important; }
          .print-running-footer { position: fixed !important; bottom: 0; left: 0; right: 0; z-index: 9999; background: white !important; }
          [data-testid="report-content"] {
            padding-top: ${hH + 2}mm !important;
            padding-bottom: ${fH + 2}mm !important;
            padding-left: 8mm !important;
            padding-right: 8mm !important;
          }
          [data-testid="report-sections"] > * { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>

      <div className="hidden print:block">
        <PrintRunningHeader lab={lab} report={report} />
        <PrintRunningFooter lab={lab} report={report} />
      </div>

      {/* Public meta bar — sharable trust cue + audio + language toggle (hidden in print) */}
      <div className="bg-white border-b border-border print:hidden" data-testid="public-meta-bar">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 min-h-12 py-2 flex items-center justify-between gap-2 text-xs flex-wrap">
          <div className="text-ink/60 flex items-center gap-1.5 min-w-0 shrink"><ShieldCheck className="w-3.5 h-3.5 text-success shrink-0" /> <span className="truncate">Verified · Shared by {lab.name}</span></div>
          <div className="flex items-center gap-1.5 shrink-0">
            {(report.has_audio_en || report.has_audio_hi) && (
              <>
                <div className="flex rounded-md border border-border p-0.5 bg-white">
                  <button data-testid="pub-lang-en" onClick={() => switchLang("en")} className={`text-[11px] px-2 py-0.5 rounded ${lang === "en" ? "bg-ink text-white" : "text-ink/60"}`} disabled={!report.has_audio_en}>EN</button>
                  <button data-testid="pub-lang-hi" onClick={() => switchLang("hi")} className={`text-[11px] px-2 py-0.5 rounded ${lang === "hi" ? "bg-ink text-white" : "text-ink/60"}`} disabled={!report.has_audio_hi}>हिं</button>
                </div>
                <Button data-testid="pub-audio-play" onClick={togglePlay} size="sm" variant="outline" className="h-7 text-xs">
                  {playing ? <Pause className="w-3 h-3 mr-1" /> : <Play className="w-3 h-3 mr-1" />} Listen
                </Button>
                <audio ref={audioRef} src={audioUrl || undefined} onEnded={() => setPlaying(false)} className="hidden" />
              </>
            )}
            <Button data-testid="pub-share-btn" onClick={handleShare} size="sm" variant="outline" className="h-7 text-xs">
              {copiedShare ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Share2 className="w-3 h-3 mr-1" /> Share</>}
            </Button>
            <Button data-testid="pub-pdf-btn" onClick={handleDownloadPDF} disabled={pdfBusy} size="sm" variant="outline" className="h-7 text-xs">
              {pdfBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />} PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Report canvas — same structure as the authenticated route so patients & referring doctors see the same page. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 print:max-w-none print:p-0 overflow-x-hidden" data-testid="report-canvas">
        {/* Phase-B: smart EFGH-user detection — appears only when report contact matches an existing user */}
        <div className="mb-4 print:hidden">
          <ClaimReportBanner token={token} onOpen={() => setClaimOpen(true)} />
        </div>
        {/* Public-share header — replaces the full letterhead ONLY on the shared route.
            Renders the lab's logo (left), an optional promotional banner (center) and a booking/customer-care
            phone CTA (right). Falls back gracefully when any of these are unset. */}
        {lab && (
          <div className="-mx-4 sm:-mx-6 lg:-mx-8 mb-4 print:hidden">
            <PublicSharedHeader lab={lab} />
          </div>
        )}

        <div data-testid="report-content" style={{ position: "relative", zIndex: 1 }}>
          <PatientMetaCard report={report} smart={smart} healthScore={healthScore} band={band} />

          <div className="space-y-6 print:space-y-4" data-testid="report-sections">
            <ReportSection title="Smart Health Summary" testid="section-summary">
              <SummaryTab smart={smart} healthScore={healthScore} />
            </ReportSection>
            {(smart.biomarkers || []).length > 0 && (
              <ReportSection title="Overall Health Snapshot" testid="section-bodymap">
                <InteractiveBodyMap smart={smart} history={[]} />
              </ReportSection>
            )}
            {(smart.biomarkers || []).length > 0 && (
              <ReportSection title="Biomarker Details" testid="section-biomarkers">
                <BiomarkersTab smart={smart} onSelect={setSelectedBio} history={[]} />
              </ReportSection>
            )}
            {(smart.critical_findings || []).length > 0 && (
              <ReportSection title="Critical Findings" testid="section-critical">
                <CriticalTab smart={smart} onSelect={setSelectedBio} history={[]} />
              </ReportSection>
            )}
            {(smart.diet_recommendations?.length || smart.physical_activity?.length || smart.avoid_watch_out?.length) > 0 && (
              <ReportSection title="Lifestyle &amp; Food" testid="section-lifestyle">
                <LifestyleTab smart={smart} />
              </ReportSection>
            )}
            {(smart.follow_up_tests || []).length > 0 && (
              <ReportSection title="Book Follow-Up Tests" testid="section-followup">
                <FollowUpTab smart={smart} report={report} lab={lab} />
              </ReportSection>
            )}
          </div>

          <ReportIntegrityFooter smart={smart} />

          <p className="mt-10 text-xs text-ink/50 text-center max-w-2xl mx-auto">
            <b>Medical Disclaimer:</b> This report is generated by EFGH&apos;s AI for educational purposes only. Always consult a qualified healthcare professional before making any health decisions.
          </p>
        </div>

        {/* Digital letterhead footer (hidden in print) */}
        {lab && lab.letterhead_mode !== "custom" && (
          <div className="-mx-4 sm:-mx-6 lg:-mx-8 mt-6 print:hidden" data-testid="lab-letterhead-bottom-onscreen">
            <LabLetterheadBottom lab={lab} report={report} />
          </div>
        )}
      </div>

      <BiomarkerModal bio={selectedBio} history={[]} onClose={() => setSelectedBio(null)} />
      <ClaimReportModal token={token} open={claimOpen} onClose={() => setClaimOpen(false)} />

      {/* ═══ DETERMINISTIC PDF ENGINE — identical to /report/:id (react-to-print clones this hidden root) ═══ */}
      {smart && (
        <PrintDocument
          ref={printRef}
          report={report}
          lab={lab}
          smart={smart}
          healthScore={healthScore}
          band={band}
          history={[]}
        />
      )}
    </div>
  );
}
